import React from 'react';
import { CardSkeleton } from '../../components/Skeleton';

// Shared building blocks for the « Statistique de ventes » / « Statistique
// générale » pages. Data-dense light dashboard language: white cards
// (Composants-2 §5), tabular figures, palette Ki Para only, NO purple
// anywhere (owner rule).

export const fmt = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;

// Compact money for tight spots (bars, mini charts): 12.4k / 1.2M.
export const fmtK = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 10_000) return `${(v / 1_000).toFixed(1)}k`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(2)}k`;
  return `${Math.round(v)}`;
};

export const PERIODS = [
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
  { key: 'month', label: 'Ce mois' },
  { key: 'year', label: 'Cette année' },
];

// Tons = les 4 variantes de carte KPI du kit (Composants-2 §5) + les teintes
// de section/barres. Literal Tailwind classes (no template strings: the
// purge scanner only keeps classes it can read verbatim).
//   white = information (valeur encre) · green = points / tri positif ·
//   coral = crédit dû · (active = tri actif, via la prop `active`).
// Les anciens noms (emerald/teal/sky/rose/orange/slate) sont résolus par
// toneOf() vers ces variantes — plus aucune classe sky/teal/emerald.
// 2026-09-08 (owner, Studio) : les pastilles d'icône de Section en vert et en
// or passent au fond BLANC — seule l'icône reste colorée. `bar` et `card` ne
// bougent pas. blue et coral gardent leur fond teinté pour l'instant.
export const TONES = {
  white: { card: '', chip: 'bg-gray-100 text-iface-600', bar: 'bg-gray-500' },
  green: { card: 'kpi-green', chip: 'bg-white text-green-700', bar: 'bg-green-600' },
  coral: { card: 'kpi-coral', chip: 'bg-red-100 text-red-600', bar: 'bg-red-600' },
  blue:  { card: '', chip: 'bg-blue-50 text-blue-600', bar: 'bg-blue-600' },
  amber: { card: '', chip: 'bg-white text-amber-600', bar: 'bg-amber-500' },
};
const TONE_ALIAS = {
  emerald: 'green', teal: 'green', sky: 'blue', rose: 'coral', red: 'coral',
  orange: 'amber', slate: 'white', gray: 'white',
};
export const toneOf = (tone) => TONES[tone] || TONES[TONE_ALIAS[tone]] || TONES.white;
// Cartes KPI d'information : seules green / coral teintent la carte ; les
// autres tons (dont les alias) restent blanches — teintée = cliquable-tri.
const cardToneOf = (tone) => (tone === 'green' || tone === 'coral' ? TONES[tone] : TONES.white);

// % change vs previous period. `invert` flips the good/bad colors for metrics
// where going UP is bad (coûts, charges) — the arrow keeps the real direction.
export const Delta = ({ curr, prev, invert = false }) => {
  if (curr == null || prev == null) return null;
  if (!curr && !prev) return null;
  const pct = prev > 0 ? ((curr - prev) / prev) * 100 : (curr > 0 ? 100 : 0);
  const up = curr > prev;
  const good = invert ? !up : up;
  // Tendance (livre §04) : texte seul 700 — ▲ +N% / ▼ −N% / = 0% — la
  // couleur suit bon/mauvais, le triangle garde la vraie direction (jamais
  // la couleur seule, pas d'icône Lucide).
  if (Math.abs(pct) < 0.5) return <span title="vs période précédente" className="bdg bdg-flat">= 0%</span>;
  return (
    <span
      title="vs période précédente"
      className={`bdg ${good ? 'bdg-up' : 'bdg-down'}`}
    >
      {up ? '▲ +' : '▼ −'}{Math.abs(pct).toFixed(0)}%
    </span>
  );
};

// Carte KPI (Composants-2 §5) : icône 16px en haut à droite, libellé 11px
// espacé, valeur 26px 700 tabulaire sur une ligne, sous-ligne 11.5px.
// `valueClassName` teinte LA VALEUR seule (owner 2026-09-07) — le montant
// prend la couleur de son sens, la carte reste blanche.
// `tone` green/coral = carte teintée (cliquable-tri), `active` = tri actif
// (bordée Spruce + drapeau). Les couleurs viennent des classes kpi-*.
// `layout="stock"` (owner 2026-09-10, /statistiques/generale) : même
// disposition que la case Stock — icône + libellé centrés sur une ligne,
// valeur centrée (18px), puis sous-ligne / enfants sous un filet gray-100,
// centrés en 11px. Les couleurs (valueClassName, tons) ne changent pas.
export const StatCard = ({ icon: Icon, label, value, valueClassName = '', tone = 'white', curr, prev, invert, sub, active = false, onClick, className = '', layout = 'default', children }) => {
  const t = cardToneOf(tone);
  const Tag = onClick ? 'button' : 'div';
  if (layout === 'stock') {
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={`kpi min-w-0 text-center ${t.card} ${active ? 'kpi-active' : ''} ${className}`}
      >
        {active && <span className="kpi-flag">TRI ACTIF ↓</span>}
        <div className="flex items-center justify-center gap-2 mb-2">
          {Icon && (
            <span className="kpi-icon static h-7 w-7 shrink-0 rounded-lg flex items-center justify-center">
              <Icon size={12} />
            </span>
          )}
          <span className="kpi-label uppercase">{label}</span>
        </div>
        <div className="flex items-baseline justify-center gap-2 min-w-0">
          <span className={`kpi-value text-lg truncate ${valueClassName}`}>{value}</span>
          <Delta curr={curr} prev={prev} invert={invert} />
        </div>
        {(sub || children) && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 text-center min-w-0">
            {sub && <div className="kpi-sub truncate">{sub}</div>}
            {children}
          </div>
        )}
      </Tag>
    );
  }
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`kpi min-w-0 text-left ${t.card} ${active ? 'kpi-active' : ''} ${className}`}
    >
      {active && <span className="kpi-flag">TRI ACTIF ↓</span>}
      {Icon && (
        <span className="kpi-icon h-7 w-7 rounded-lg flex items-center justify-center">
          <Icon size={12} />
        </span>
      )}
      <div className="kpi-label uppercase truncate pr-10 mb-2">{label}</div>
      <div className="flex items-baseline gap-2 min-w-0">
        <span className={`kpi-value text-xl truncate ${valueClassName}`}>{value}</span>
        <Delta curr={curr} prev={prev} invert={invert} />
      </div>
      {sub && <div className="kpi-sub mt-1 truncate">{sub}</div>}
      {children}
    </Tag>
  );
};

// Section wrapper: white card + titled header (icon tinted by tone).
export const Section = ({ icon: Icon, title, tone = 'white', right, children, className = '' }) => {
  const t = toneOf(tone);
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 p-5 min-w-0 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2 min-w-0">
          {Icon && (
            <span className={`h-7 w-7 shrink-0 rounded-lg flex items-center justify-center ${t.chip}`}>
              <Icon size={12} />
            </span>
          )}
          <span className="truncate">{title}</span>
        </h2>
        {right && <div className="shrink-0 text-xs text-gray-400">{right}</div>}
      </div>
      {children}
    </div>
  );
};

// Gray chip title — same design as the other pages (owner Studio:
// chip = refresh button). `right` : contenu aligné à droite sur la même
// ligne (ex. bouton « Historique d'action » de la page générale).
export const StatsChip = ({ icon: Icon, title, right = null }) => (
  <div className="flex flex-wrap items-center gap-3 mb-4">
    {/* Chip = refresh button (owner Studio 2026-08-13; pages « ajouter » excluded) */}
    <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm">
      <Icon size={13} /> {title}
    </button>
    {right && <div className="ml-auto flex items-center gap-3">{right}</div>}
  </div>
);

// Segmented view switch (Barres/Courbe, Catégories/Top produits…) — same
// look as the app's Produits/Services toggles.
export const ViewToggle = ({ value, onChange, options, label = 'Vue' }) => (
  <div className="inline-flex bg-gray-100 rounded-xl p-1" role="tablist" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        role="tab"
        aria-selected={value === o.key}
        onClick={() => onChange(o.key)}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
          value === o.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const Spinner = () => (
  <div className="text-center py-20">
    <CardSkeleton />
  </div>
);

// Horizontal labeled bars — one hue, amount + % share on the right, and the
// exact figures in the row title (hover). rows: [{name, value, extra?}].
export const HBarList = ({ rows, tone = 'green', emptyText = 'Aucune donnée.', cols = 1 }) => {
  const t = toneOf(tone);
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return <p className="text-gray-400 text-sm text-center py-8">{emptyText}</p>;
  const max = Math.max(1, ...list.map((r) => r.value || 0));
  const total = list.reduce((s, r) => s + (r.value || 0), 0);
  return (
    <div className={cols === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3' : 'space-y-3'}>
      {list.map((r, i) => {
        const share = total > 0 ? ((r.value || 0) / total) * 100 : 0;
        return (
          <div key={i} title={`${r.name} — ${fmt(r.value)}${r.extra ? ` · ${r.extra}` : ''}`}>
            <div className="flex justify-between items-baseline gap-2 text-sm mb-1">
              <span className="font-medium text-gray-700 truncate">{r.name}</span>
              <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                {fmtK(r.value)} <span className="text-gray-400">· {share.toFixed(0)}%</span>
              </span>
            </div>
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${t.bar}`}
                style={{ width: `${Math.min(100, ((r.value || 0) / max) * 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
